import { Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import type { MBCUIMessage } from "../types/mbc-chat.types";
import type { MbcUploadBinding } from "./parts/UploadPromptPart";

import { getVisibleText } from "../lib/message-content";
import { MBCMessageBubble } from "./MBCMessageBubble";

const PINNED_THRESHOLD_PX = 80;

type MBCMessageListProps = {
  isAlpha?: boolean;
  isInteractiveDisabled: boolean;
  isStreaming: boolean;
  isUploading: boolean;
  messages: MBCUIMessage[];
  onReview: () => void;
  onSelectChoice: (label: string) => void;
  upload?: MbcUploadBinding;
};

export function MBCMessageList({
  isAlpha = false,
  isInteractiveDisabled,
  isStreaming,
  isUploading,
  messages,
  onReview,
  onSelectChoice,
  upload,
}: MBCMessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastAssistantMessageId = [...messages]
    .reverse()
    .find((message) => message.role === "assistant")?.id;

  const lastMessage = messages.at(-1);
  const isAwaitingFirstContent =
    isStreaming &&
    (lastMessage?.role !== "assistant" || getVisibleText(lastMessage) === "");

  const contentRef = useRef<HTMLDivElement>(null);
  const isPinnedToBottom = useRef(true);
  const previousLastMessageId = useRef(lastMessage?.id);
  const [initialMessageIds] = useState(
    () => new Set(messages.map((message) => message.id)),
  );

  const scrollToBottom = () => {
    const container = containerRef.current;

    if (container) container.scrollTop = container.scrollHeight;
  };

  const handleScroll = () => {
    const container = containerRef.current;

    if (!container) return;
    isPinnedToBottom.current =
      container.scrollHeight - container.scrollTop - container.clientHeight <=
      PINNED_THRESHOLD_PX;
  };

  useEffect(() => {
    const hasNewUserMessage =
      lastMessage?.role === "user" &&
      lastMessage.id !== previousLastMessageId.current;

    previousLastMessageId.current = lastMessage?.id;
    if (hasNewUserMessage) isPinnedToBottom.current = true;
    if (isPinnedToBottom.current) scrollToBottom();
  }, [messages, isStreaming]);

  useEffect(() => {
    const content = contentRef.current;

    if (!content) return;
    const observer = new ResizeObserver(() => {
      if (isPinnedToBottom.current) scrollToBottom();
    });

    observer.observe(content);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="flex flex-1 flex-col overflow-y-auto"
      onScroll={handleScroll}
      ref={containerRef}
    >
      <div
        className={twMerge(
          "flex flex-col gap-lg px-md py-xl lg:px-3xl",
          isAlpha && "pb-8xl",
        )}
        ref={contentRef}
      >
        {messages.map((message) => (
          <MBCMessageBubble
            isAlpha={isAlpha}
            isInteractiveDisabled={isInteractiveDisabled}
            isLastAssistantMessage={
              message.role === "assistant" &&
              message.id === lastAssistantMessageId
            }
            isStreaming={isStreaming}
            isUploading={isUploading}
            key={message.id}
            message={message}
            onReview={onReview}
            onSelectChoice={onSelectChoice}
            shouldAnimateEntrance={!initialMessageIds.has(message.id)}
            upload={upload}
          />
        ))}

        {isAwaitingFirstContent ? (
          <div
            aria-label="Assistant is typing"
            className="flex w-full items-center gap-md"
            role="status"
          >
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-utility-brand-100 text-fg-brand-primary">
              <Sparkles className="size-4" />
            </div>
            <div className="flex items-center gap-xs">
              <span className="size-2 rounded-full bg-fg-quaternary motion-safe:animate-bounce" />
              <span className="size-2 rounded-full bg-fg-quaternary [animation-delay:150ms] motion-safe:animate-bounce" />
              <span className="size-2 rounded-full bg-fg-quaternary [animation-delay:300ms] motion-safe:animate-bounce" />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
